import React, { useState, useEffect } from "react";
import { Phone, MessageSquare, HelpCircle, Shield } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Persistent Communication Bar
 * 
 * Always accessible from all caregiver screens.
 * Provides quick access to:
 * - Call Office
 * - Message Office
 * - Need Help
 * - Emergency Assistance (opens configurable workflow, NOT auto-911)
 */
export default function CommunicationBar({ 
  onCallOffice, 
  onMessageOffice, 
  onNeedHelp, 
  onEmergency 
}) {
  const [officeContact, setOfficeContact] = useState(null);

  useEffect(() => {
    loadOfficeContact();
  }, []);

  const loadOfficeContact = async () => {
    try {
      const { data } = await api.get("/evv/office-contact");
      setOfficeContact(data);
    } catch (err) {
      console.warn("Could not load office contact:", err);
    }
  };

  const handleCallOffice = () => {
    if (officeContact?.office_phone) {
      window.location.href = `tel:${officeContact.office_phone}`;
    } else if (officeContact?.on_call_phone) {
      window.location.href = `tel:${officeContact.on_call_phone}`;
    } else if (onCallOffice) {
      onCallOffice();
    } else {
      alert("Office phone number not configured. Please contact your supervisor.");
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      {/* Communication Actions */}
      <div className="bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-lg mx-auto px-2 py-2">
          <div className="grid grid-cols-4 gap-1">
            {/* Call Office */}
            <button
              onClick={handleCallOffice}
              className="flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors"
              data-testid="comm-call-office"
            >
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                <Phone className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-xs font-medium text-gray-700">Call Office</span>
            </button>

            {/* Message Office */}
            <button
              onClick={onMessageOffice}
              className="flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors"
              data-testid="comm-message-office"
            >
              <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-xs font-medium text-gray-700">Message</span>
            </button>

            {/* Need Help */}
            <button
              onClick={onNeedHelp}
              className="flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors"
              data-testid="comm-need-help"
            >
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-xs font-medium text-gray-700">Need Help</span>
            </button>

            {/* Emergency Assistance */}
            <button
              onClick={onEmergency}
              className="flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl hover:bg-red-50 active:bg-red-100 transition-colors"
              data-testid="comm-emergency"
            >
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-600" />
              </div>
              <span className="text-xs font-medium text-red-700">Emergency</span>
            </button>
          </div>
        </div>
      </div>

      {/* Safe area spacer for iOS */}
      <div className="h-safe-area-inset-bottom bg-white" />
    </div>
  );
}
